import { useState } from 'react';
import { BrandArtwork, type ColourwayId } from '@/components/brand/BrandArtwork';
import type { ProductImage } from '@/types/catalogue';

/**
 * Product image gallery — BUILD-PLAN §27, §62.
 *
 * One component covers both required experiences:
 *   • mobile — a horizontally swipeable strip using CSS scroll-snap, which
 *     gives native momentum and no JavaScript touch handling
 *   • desktop — a large main image with thumbnail navigation
 *
 * Thumbnails are real buttons in a tablist, so the gallery is operable by
 * keyboard rather than swipe only (§40).
 */

interface ProductGalleryProps {
  images: ProductImage[];
  productName: string;
  /** Selected finish, so the placeholder matches the rest of the page. */
  colourway?: ColourwayId | undefined;
}

export function ProductGallery({ images, productName, colourway }: ProductGalleryProps) {
  const [activeIndex, setActiveIndex] = useState(0);

  if (images.length === 0) {
    return (
      // Square artwork, not the portrait card variant: the gallery frame is
      // square, and `preserveAspectRatio="slice"` would otherwise crop a
      // portrait composition until the motif fell outside the visible area.
      <div className="aspect-square w-full">
        <BrandArtwork seed={productName} ratio="square" {...(colourway ? { colourway } : {})} />
      </div>
    );
  }

  const active = images[activeIndex] ?? images[0]!;

  return (
    <div className="flex flex-col gap-4">
      {/* Mobile: swipeable strip. Hidden at md, where thumbnails take over. */}
      {/* Each slide is a snap point. The scrollbar is hidden for presentation
          but scrolling stays available to touch, wheel and keyboard. */}
      <div className="no-scrollbar -mx-5 flex snap-x snap-mandatory gap-3 overflow-x-auto px-5 md:hidden">
        {images.map((image) => (
          <img
            key={image.id}
            src={image.url}
            alt={image.alt}
            loading="lazy"
            decoding="async"
            className="aspect-square w-[85%] shrink-0 snap-center object-cover"
          />
        ))}
      </div>

      {/* Desktop: main image */}
      {/* Boutique framing: depth plus an inset keyline, matching the product
          cards and occasion tiles so every image on the site is presented the
          same way. */}
      <div className="relative hidden aspect-square w-full overflow-hidden bg-surface-muted shadow-card md:block">
        <img
          key={active.id}
          src={active.url}
          alt={active.alt || productName}
          // The main image is the page's LCP element — never lazy.
          loading="eager"
          fetchPriority="high"
          decoding="async"
          className="h-full w-full object-cover"
        />

        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-3 border border-ivory/20"
        />
      </div>

      {images.length > 1 && (
        <div
          role="tablist"
          aria-label={`${productName} images`}
          className="hidden gap-3 md:flex"
        >
          {images.map((image, index) => (
            <button
              key={image.id}
              type="button"
              role="tab"
              aria-selected={index === activeIndex}
              aria-label={`View image ${index + 1} of ${images.length}`}
              onClick={() => setActiveIndex(index)}
              className={`h-20 w-20 overflow-hidden border transition-colors ${
                index === activeIndex
                  ? 'border-accent'
                  : 'border-transparent hover:border-line'
              }`}
            >
              <img
                src={image.url}
                alt=""
                loading="lazy"
                decoding="async"
                className="h-full w-full object-cover"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
